import { useEffect, useState, useCallback } from "react";
import { Loader2, FileBarChart, Send, MessageSquareWarning, Scale, Wifi, ShieldAlert, Settings2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatCard } from "@/components/common/StatCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { CredencialDialog } from "@/components/integraciones/CredencialDialog";
import { useAuth } from "@/context/AuthContext";
import { TooltipButton } from "@/components/common/Tooltips";
import { api, listResource, apiError } from "@/lib/api";
import { fdatetime } from "@/lib/format";
import { toast } from "sonner";

const MODO_META = {
  mock: { label: "Modo demo (simulación)", cls: "bg-slate-100 text-slate-600" },
  sandbox: { label: "Sandbox (envío real)", cls: "bg-amber-100 text-amber-700" },
  produccion: { label: "Producción (envío real)", cls: "bg-forest-600/15 text-forest-700" },
};

// Mapeo tarjeta → proveedor con adaptador real conmutable por entidad (mock·sandbox·producción)
const CARD_PROVEEDOR = {
  "CONDUSEF REUNE": "condusef",
  "CONDUSEF REDECO": "condusef",
  "Círculo de Crédito": "circulo",
  "Buró de Crédito": "buro_pf",
  "Buró de Crédito Empresas": "buro_pm",
  "PUI / RENAPO": "pui",
};

function ModoBadge({ modo }) {
  const m = MODO_META[modo] || MODO_META.mock;
  return <span data-testid={`modo-badge-${modo || "mock"}`} className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.cls}`}>{m.label}</span>;
}

export default function CentroIntegraciones() {
  const { user } = useAuth();
  const puedeConfig = ["admin_entidad", "tecnico"].includes(user?.role);
  const [resumen, setResumen] = useState(null);
  const [consultas, setConsultas] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [reportesCartera, setReportesCartera] = useState([]);
  const [conexiones, setConexiones] = useState([]);
  const [cfgProv, setCfgProv] = useState(null); // proveedor a configurar (CredencialDialog)
  const [modos, setModos] = useState({}); // proveedor -> modo (mock|sandbox|produccion)
  const [cartForm, setCartForm] = useState({ sociedad: "Círculo de Crédito", periodo: "2026-06" });
  const [busy, setBusy] = useState(false);
  const [redeco, setRedeco] = useState({ cliente: "", credito: "", despacho: "", motivo: "" });
  const [reune, setReune] = useState({ cliente: "", tipo: "Reclamación", producto: "Crédito Personal", causa: "" });
  const [confirm, setConfirm] = useState(null); // { title, description, action }

  const loadAll = useCallback(() => {
    api.get("/dashboard/integraciones").then((r) => setResumen(r.data)).catch(() => {});
    listResource("sic/consultas").then(setConsultas).catch(() => {});
    api.get("/integraciones/eventos").then((r) => setEventos(r.data)).catch(() => {});
    listResource("sic/reportes-cartera").then(setReportesCartera).catch(() => {});
    listResource("integraciones").then(setConexiones).catch(() => {});
    api.get("/integraciones/credenciales").then((r) => {
      const map = {};
      (r.data || []).forEach((c) => { map[c.proveedor] = c.modo; });
      setModos(map);
    }).catch(() => {});
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  // Helpers de modo / honestidad
  const provDeSociedad = (soc) =>
    soc === "Buró de Crédito" ? "buro_pf" : soc === "Buró Empresas / PM-PFAE" ? "buro_pm" : "circulo";
  const modoDe = (prov) => modos[prov] || "mock";
  const esReal = (prov) => modoDe(prov) !== "mock";

  const askConfirm = (title, description, action) => setConfirm({ title, description, action });
  const runConfirm = async () => {
    const fn = confirm?.action;
    setConfirm(null);
    if (fn) await fn();
  };

  // ---------------- Cartera → SIC ----------------
  const doCartera = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sic/reporte-cartera", cartForm);
      toast.success(`Reporte ${data.folio}: ${data.registros} registros — ${data.estatus} (${data.modo})`);
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const generarCartera = () => {
    const prov = provDeSociedad(cartForm.sociedad);
    if (esReal(prov)) {
      askConfirm(
        `Validar cartera para ${cartForm.sociedad}`,
        `La integración de ${cartForm.sociedad} está en ${MODO_META[modoDe(prov)].label}. ` +
        `Se validará la cartera del periodo ${cartForm.periodo}; el envío real está pendiente de configurar la ruta de cartera contra el sandbox del proveedor. ¿Continuar?`,
        doCartera
      );
    } else { doCartera(); }
  };

  // ---------------- REDECO ----------------
  const doRedeco = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/condusef/redeco/queja", redeco);
      toast.success(`Queja REDECO ${data.folio} creada. Caso, alerta y calendario actualizados.`);
      setRedeco({ cliente: "", credito: "", despacho: "", motivo: "" });
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const enviarRedeco = () => {
    if (!redeco.cliente || !redeco.motivo) { toast.error("Captura cliente y motivo"); return; }
    if (esReal("condusef")) {
      askConfirm(
        "Confirmar envío real a CONDUSEF (REDECO)",
        `Estás por enviar esta queja REDECO a CONDUSEF en ${MODO_META[modoDe("condusef")].label}. ` +
        `Es un envío real a la autoridad y quedará registrado en bitácora. ¿Deseas continuar?`,
        doRedeco
      );
    } else { doRedeco(); }
  };

  // ---------------- REUNE ----------------
  const doReune = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/condusef/reune/registro", reune);
      toast.success(`Registro REUNE ${data.folio} creado. Evidencia y calendario actualizados.`);
      setReune({ cliente: "", tipo: "Reclamación", producto: "Crédito Personal", causa: "" });
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const enviarReune = () => {
    if (!reune.cliente || !reune.causa) { toast.error("Captura cliente y causa"); return; }
    if (esReal("condusef")) {
      askConfirm(
        "Confirmar envío real a CONDUSEF (REUNE)",
        `Estás por enviar este registro REUNE a CONDUSEF en ${MODO_META[modoDe("condusef")].label}. ` +
        `Es un envío real a la autoridad y quedará registrado en bitácora. ¿Deseas continuar?`,
        doReune
      );
    } else { doReune(); }
  };

  // ---------------- Cierre trimestral ----------------
  // Feb-2026 · REUNE: el cierre trimestral se hace manualmente en el portal
  // https://pur.condusef.gob.mx (no hay API oficial). El OC sube el acuse en la
  // pestaña REUNE de CONDUSEF. Aquí se eliminaron el botón y su handler.

  const probar = async (id) => {
    try { const { data } = await api.post(`/integraciones/${id}/probar`); toast.success(data.estado); loadAll(); }
    catch (e) { toast.error("Error al probar"); }
  };

  const r = resumen || {};
  const metrics = [
    { label: "Integraciones activas", value: r.integraciones_activas ?? 0, icon: "Plug", accent: "primary" },
    { label: "Consultas SIC del mes", value: r.consultas_mes ?? 0, icon: "FileSearch", accent: "blue" },
    { label: "Consultas exitosas", value: r.consultas_exitosas ?? 0, icon: "CheckCircle2", accent: "green" },
    { label: "Consultas fallidas", value: r.consultas_fallidas ?? 0, icon: "XCircle", accent: "red" },
    { label: "Clientes no hit", value: r.clientes_no_hit ?? 0, icon: "UserX", accent: "amber" },
    { label: "Score promedio", value: r.score_promedio ?? 0, icon: "Gauge", accent: "primary" },
    { label: "Quejas REDECO abiertas", value: r.quejas_redeco_abiertas ?? 0, icon: "MessageSquareWarning", accent: "amber" },
    { label: "Reclamaciones REUNE", value: r.reclamaciones_reune_abiertas ?? 0, icon: "Scale", accent: "blue" },
    { label: "Reportes cartera pend.", value: r.reportes_cartera_pendientes ?? 0, icon: "FileBarChart", accent: "amber" },
    { label: "Acuses pendientes", value: r.acuses_pendientes ?? 0, icon: "FileClock", accent: "amber" },
    { label: "Errores críticos", value: r.errores_criticos ?? 0, icon: "AlertTriangle", accent: "red" },
  ];

  const cartProv = provDeSociedad(cartForm.sociedad);
  const cartReal = esReal(cartProv);
  const condReal = esReal("condusef");

  return (
    <div>
      <PageHeader title="Centro de Integraciones Regulatorias y Crediticias"
        subtitle="Capa transversal: CONDUSEF (REUNE/REDECO), Círculo y Buró conectados con la operación de la SOFOM" />
      <div className="p-6 md:p-8">
        <Tabs defaultValue="resumen">
          <TabsList className="mb-4 flex-wrap">
            <TabsTrigger value="resumen" data-testid="ci-tab-resumen">Resumen</TabsTrigger>
            <TabsTrigger value="sic" data-testid="ci-tab-sic">Actividad SIC</TabsTrigger>
            <TabsTrigger value="cartera" data-testid="ci-tab-cartera">Cartera → SIC</TabsTrigger>
            <TabsTrigger value="condusef" data-testid="ci-tab-condusef">REUNE / REDECO</TabsTrigger>
            <TabsTrigger value="acuses" data-testid="ci-tab-acuses">Acuses y eventos</TabsTrigger>
            <TabsTrigger value="conexiones" data-testid="ci-tab-conexiones">Conexiones</TabsTrigger>
          </TabsList>

          <TabsContent value="resumen">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
              {metrics.map((m, i) => <StatCard key={m.label} testid={`ci-metric-${i}`} delay={i * 25} {...m} />)}
            </div>
            <h3 className="mb-3 mt-6 font-heading text-sm font-semibold text-slate-700">Eventos recientes</h3>
            <DataTable testid="table-eventos-recientes" rows={r.eventos_recientes || []}
              columns={[
                { key: "tipo", label: "Tipo", kind: "badge" }, { key: "sociedad", label: "Origen" },
                { key: "cliente", label: "Cliente" }, { key: "folio", label: "Folio" },
                { key: "modo", label: "Modo" },
                { key: "estatus", label: "Estatus", kind: "badge" }, { key: "modulo", label: "Módulo" },
              ]} emptyLabel="Sin eventos" />
          </TabsContent>

          <TabsContent value="sic">
            <DataTable testid="table-consultas-sic" rows={consultas}
              columns={[
                { key: "fecha", label: "Fecha", kind: "date" }, { key: "sociedad", label: "Sociedad", kind: "badge" },
                { key: "cliente", label: "Cliente" }, { key: "producto", label: "Producto" },
                { key: "score", label: "Score" }, { key: "resultado", label: "Resultado", kind: "badge" },
                { key: "modo", label: "Modo" },
                { key: "folio", label: "Folio" }, { key: "usuario", label: "Usuario" },
              ]} emptyLabel="Sin consultas a sociedades de información crediticia" />
            <p className="mt-3 text-xs text-slate-400">Cada consulta genera evidencia en el expediente, alimenta el análisis de crédito y registra bitácora.</p>
          </TabsContent>

          <TabsContent value="cartera">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-slate-700"><FileBarChart className="h-4 w-4 text-primary" /> Generar reporte de cartera</h3>
                  <ModoBadge modo={modoDe(cartProv)} />
                </div>
                <div className="space-y-3">
                  <div><Label className="mb-1 block text-xs">Sociedad</Label>
                    <Select value={cartForm.sociedad} onValueChange={(v) => setCartForm((p) => ({ ...p, sociedad: v }))}>
                      <SelectTrigger data-testid="cart-sociedad"><SelectValue /></SelectTrigger>
                      <SelectContent>{["Círculo de Crédito", "Buró de Crédito"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="mb-1 block text-xs">Periodo</Label><Input data-testid="cart-periodo" value={cartForm.periodo} onChange={(e) => setCartForm((p) => ({ ...p, periodo: e.target.value }))} /></div>
                  <Button data-testid="generar-cartera-btn" onClick={generarCartera} disabled={busy} className="w-full bg-primary hover:bg-sidebar-hover">
                    {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                    {cartReal ? "Validar cartera (envío real pendiente)" : "Validar y simular envío"}
                  </Button>
                  <p className="text-xs text-slate-400">
                    {cartReal
                      ? "Valida y prepara los registros. El envío real a la sociedad está pendiente de configurar la ruta/formato de cartera contra el sandbox del proveedor."
                      : "Modo demo: marca créditos como reportados, valida campos y guarda evidencia y acuse por crédito."}
                  </p>
                </div>
              </div>
              <div className="lg:col-span-2">
                <DataTable testid="table-reportes-cartera" rows={reportesCartera}
                  columns={[
                    { key: "sociedad", label: "Sociedad", kind: "badge" }, { key: "periodo", label: "Periodo" },
                    { key: "registros", label: "Registros" }, { key: "errores", label: "Errores" },
                    { key: "estatus", label: "Estatus", kind: "badge" }, { key: "modo", label: "Modo" },
                    { key: "folio", label: "Folio/Acuse" }, { key: "fecha", label: "Fecha", kind: "date" },
                  ]} emptyLabel="Aún no se ha reportado cartera a SIC" />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="condusef">
            <div className="mb-4 flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5">
              <span className="flex items-center gap-2 text-sm font-medium text-slate-700"><Scale className="h-4 w-4 text-forest-600" /> Estado de la conexión CONDUSEF</span>
              <ModoBadge modo={modoDe("condusef")} />
            </div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700"><MessageSquareWarning className="h-4 w-4 text-amber-600" /> Queja REDECO (desde cobranza)</h3>
                <div className="space-y-3">
                  <Input data-testid="redeco-cliente" placeholder="Cliente" value={redeco.cliente} onChange={(e) => setRedeco((p) => ({ ...p, cliente: e.target.value }))} />
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="Crédito" value={redeco.credito} onChange={(e) => setRedeco((p) => ({ ...p, credito: e.target.value }))} />
                    <Input placeholder="Despacho" value={redeco.despacho} onChange={(e) => setRedeco((p) => ({ ...p, despacho: e.target.value }))} />
                  </div>
                  <Input data-testid="redeco-motivo" placeholder="Motivo de la queja" value={redeco.motivo} onChange={(e) => setRedeco((p) => ({ ...p, motivo: e.target.value }))} />
                  <TooltipButton tooltip="Enviar la queja al portal REDECO (requiere conexión CONDUSEF activa)"
                    data-testid="redeco-enviar" onClick={enviarRedeco} disabled={busy} className="w-full bg-accent hover:bg-forest-700">
                    <Send className="mr-2 h-4 w-4" /> {condReal ? "Crear caso y enviar a CONDUSEF" : "Crear caso y simular envío REDECO"}
                  </TooltipButton>
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700"><Scale className="h-4 w-4 text-forest-600" /> Registro REUNE (UNE)</h3>
                <div className="space-y-3">
                  <Input data-testid="reune-cliente" placeholder="Cliente" value={reune.cliente} onChange={(e) => setReune((p) => ({ ...p, cliente: e.target.value }))} />
                  <div className="grid grid-cols-2 gap-3">
                    <Select value={reune.tipo} onValueChange={(v) => setReune((p) => ({ ...p, tipo: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{["Consulta", "Reclamación", "Aclaración"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input placeholder="Producto" value={reune.producto} onChange={(e) => setReune((p) => ({ ...p, producto: e.target.value }))} />
                  </div>
                  <Input data-testid="reune-causa" placeholder="Causa" value={reune.causa} onChange={(e) => setReune((p) => ({ ...p, causa: e.target.value }))} />
                  <TooltipButton tooltip="Registrar la reclamación en REUNE de CONDUSEF"
                    data-testid="reune-enviar" onClick={enviarReune} disabled={busy} className="w-full bg-primary hover:bg-sidebar-hover">
                    <Send className="mr-2 h-4 w-4" /> {condReal ? "Registrar y enviar a CONDUSEF" : "Registrar y simular envío REUNE"}
                  </TooltipButton>
                </div>
              </div>
            </div>

            {/* Cierre trimestral — feb-2026: eliminado. CONDUSEF no expone endpoint
                de cierre por API; se realiza manualmente en el portal
                https://pur.condusef.gob.mx y el acuse se sube en la pestaña REUNE
                del módulo CONDUSEF. */}
          </TabsContent>

          <TabsContent value="acuses">
            <DataTable testid="table-acuses" rows={eventos}
              columns={[
                { key: "fecha", label: "Fecha", kind: "date" }, { key: "tipo", label: "Tipo", kind: "badge" },
                { key: "sociedad", label: "Origen" }, { key: "cliente", label: "Cliente" },
                { key: "folio", label: "Folio" }, { key: "acuse", label: "Acuse" }, { key: "modo", label: "Modo" },
                { key: "estatus", label: "Estatus", kind: "badge" }, { key: "modulo", label: "Módulo" },
              ]} emptyLabel="Sin acuses ni respuestas registradas" />
          </TabsContent>

          <TabsContent value="conexiones">
            <div className="mb-4 rounded-md border border-sage/40 bg-sage/15 px-4 py-2.5 text-sm text-forest-600">
              Las tarjetas de CONDUSEF, Buró, Círculo y PUI/RENAPO cuentan con <strong>adaptador real</strong>. En <strong>modo demo (mock)</strong> operan simuladas;
              carga las credenciales institucionales de tu entidad y cambia a <strong>sandbox/producción</strong> para conectar de verdad.
              FELVORA orquesta; la relación con la autoridad o el buró corresponde al sujeto obligado.
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {conexiones.map((i) => {
                const prov = CARD_PROVEEDOR[i.nombre];
                return (
                  <div key={i.id} data-testid={`ci-conexion-${i.id}`} className="flex flex-col rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex items-start justify-between">
                      <span className="font-heading text-sm font-semibold text-slate-800">{i.nombre}</span>
                      <StatusBadge value={i.estatus} />
                    </div>
                    <p className="mt-1 text-xs text-slate-400">{i.ambiente} · {i.categoria}{prov ? " · Adaptador real" : ""}</p>
                    <div className="mt-2 flex-1 space-y-1 text-xs text-slate-500">
                      <p>Última conexión: {i.ultima_conexion ? fdatetime(i.ultima_conexion) : "—"}</p>
                      {i.ultimo_resultado && <p className="text-emerald-600">{i.ultimo_resultado}</p>}
                    </div>
                    {prov ? (
                      <TooltipButton tooltip="Configurar las llaves de la API (se cifran en reposo)"
                        data-testid={`ci-configurar-${prov}-${i.id}`} variant="outline" size="sm" className="mt-3 w-full"
                        onClick={() => setCfgProv(prov)} disabled={!puedeConfig}>
                        <Settings2 className="mr-1.5 h-4 w-4" /> {puedeConfig ? "Configurar conexión" : "Solo admin / técnico"}
                      </TooltipButton>
                    ) : (
                      <TooltipButton tooltip="Probar la conexión con la API real (verifica que las credenciales sean válidas)"
                        data-testid={`ci-probar-${i.id}`} variant="outline" size="sm" className="mt-3 w-full" onClick={() => probar(i.id)}>
                        <Wifi className="mr-1.5 h-4 w-4" /> Probar conexión
                      </TooltipButton>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <CredencialDialog proveedor={cfgProv} open={!!cfgProv} onOpenChange={(o) => !o && setCfgProv(null)} onSaved={loadAll} />

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent data-testid="confirm-envio-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 font-heading">
              <ShieldAlert className="h-5 w-5 text-amber-600" /> {confirm?.title}
            </AlertDialogTitle>
            <AlertDialogDescription>{confirm?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancelar">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-aceptar" onClick={runConfirm} className="bg-accent hover:bg-forest-700">
              Confirmar envío
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
